/**
 * Notification store
 * Manages toast notifications and alerts
 */

import { create } from 'zustand';
import toast from 'react-hot-toast';

/**
 * Notification types
 */
export type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'loading';

/**
 * Notification item
 */
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: Date;
}

/**
 * Notification history item
 */
export interface NotificationHistoryItem {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  createdAt: Date;
  read: boolean;
}

/**
 * Notification state interface
 */
interface NotificationState {
  // Notification history
  history: NotificationHistoryItem[];
  unreadCount: number;

  // Actions
  notify: (type: NotificationType, title: string, message?: string, options?: NotifyOptions) => string;
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  loading: (title: string) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearHistory: () => void;
}

/**
 * Options for notify function
 */
interface NotifyOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  addToHistory?: boolean;
}

/**
 * Default durations by type
 */
const defaultDurations: Record<NotificationType, number> = {
  success: 4000,
  error: 6000,
  info: 4000,
  warning: 5000,
  loading: Infinity,
};

/**
 * Maximum history items to keep
 */
const MAX_HISTORY_ITEMS = 50;

/**
 * Generate unique ID
 */
function generateId(): string {
  return `notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Notification store
 */
export const useNotificationStore = create<NotificationState>((set, get) => ({
  history: [],
  unreadCount: 0,

  notify: (type, title, message, options = {}) => {
    const id = generateId();
    const { duration = defaultDurations[type], action, addToHistory = true } = options;

    // Create toast with react-hot-toast
    const toastContent = message ? `${title}\n${message}` : title;

    switch (type) {
      case 'success':
        toast.success(toastContent, { id, duration });
        break;
      case 'error':
        toast.error(toastContent, { id, duration });
        break;
      case 'loading':
        toast.loading(toastContent, { id });
        break;
      case 'warning':
      case 'info':
      default:
        toast(toastContent, {
          id,
          duration,
          icon: type === 'warning' ? '⚠️' : 'ℹ️',
        });
    }

    // Add to history if not a loading toast
    if (addToHistory && type !== 'loading') {
      set((state) => {
        const newItem: NotificationHistoryItem = {
          id,
          type,
          title,
          message,
          createdAt: new Date(),
          read: false,
        };

        const updatedHistory = [newItem, ...state.history].slice(0, MAX_HISTORY_ITEMS);

        return {
          history: updatedHistory,
          unreadCount: state.unreadCount + 1,
        };
      });
    }

    // Handle action if provided
    if (action) {
      // Could enhance toast to show action button
      // For now, action is available in history
    }

    return id;
  },

  success: (title, message) => {
    return get().notify('success', title, message);
  },

  error: (title, message) => {
    return get().notify('error', title, message);
  },

  info: (title, message) => {
    return get().notify('info', title, message);
  },

  warning: (title, message) => {
    return get().notify('warning', title, message);
  },

  loading: (title) => {
    return get().notify('loading', title, undefined, { addToHistory: false });
  },

  dismiss: (id) => {
    toast.dismiss(id);
  },

  dismissAll: () => {
    toast.dismiss();
  },

  markAsRead: (id) => {
    set((state) => {
      const updatedHistory = state.history.map((item) =>
        item.id === id ? { ...item, read: true } : item
      );
      const unreadCount = updatedHistory.filter((item) => !item.read).length;

      return { history: updatedHistory, unreadCount };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      history: state.history.map((item) => ({ ...item, read: true })),
      unreadCount: 0,
    }));
  },

  clearHistory: () => {
    set({ history: [], unreadCount: 0 });
  },
}));

/**
 * Helper hook for common notification patterns
 */
export function useNotification() {
  const { success, error, info, warning, loading, dismiss, dismissAll } =
    useNotificationStore();

  /**
   * Show a loading toast, execute async action, then show success/error
   */
  const promise = async <T>(
    asyncFn: () => Promise<T>,
    {
      loading: loadingMsg = 'Loading...',
      success: successMsg = 'Success!',
      error: errorMsg = 'Something went wrong',
    }: {
      loading?: string;
      success?: string | ((data: T) => string);
      error?: string | ((err: Error) => string);
    } = {}
  ): Promise<T> => {
    const toastId = loading(loadingMsg);

    try {
      const result = await asyncFn();
      dismiss(toastId);

      const message = typeof successMsg === 'function' ? successMsg(result) : successMsg;
      success(message);

      return result;
    } catch (err) {
      dismiss(toastId);

      const message =
        typeof errorMsg === 'function'
          ? errorMsg(err as Error)
          : (err as Error).message || errorMsg;
      error(message);

      throw err;
    }
  };

  return {
    success,
    error,
    info,
    warning,
    loading,
    dismiss,
    dismissAll,
    promise,
  };
}

/**
 * Hook for notification history
 */
export function useNotificationHistory() {
  const history = useNotificationStore((state) => state.history);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const markAsRead = useNotificationStore((state) => state.markAsRead);
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead);
  const clearHistory = useNotificationStore((state) => state.clearHistory);

  return {
    history,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearHistory,
  };
}
